import { memo } from 'react';
import { useWidthCheck } from '../../../hooks';
import { MOBILE_WIDTH } from '../../../settings';
import cx from '@utils/cx.util';
import { CMSHeader } from './types';
import { Markdown } from '@features/markdown/components/Markdown';
import { useVariation } from '@hooks/useVariation';

type Props = {
  component: CMSHeader;
};

const LifestyleHeader = ({ component }: Props) => {
  const {
    attributes: { title, position = 'center', image, video },
  } = component;

  const isEnhancedLifestyleHeader = useVariation('Enhanced PDP - Lifestyle Header');
  const isMobView = useWidthCheck(MOBILE_WIDTH);

  if (!title || !image?.desktop) return null;

  return (
    <div
      className={cx(
        'tw-relative tw-mx-auto tw-flex tw-h-[250px] tw-max-w-[1920px] tw-justify-center tw-from-black tw-px-4 tw-py-3 lg:tw-h-[550px] lg:tw-py-5',
        {
          'tw-rounded-3xl lg:tw-h-[440px]': isEnhancedLifestyleHeader,
        },
        {
          'tw-justify-start': ['bottom-left', 'left', 'top-left'].includes(position),
          'tw-justify-end': ['bottom-right', 'right', 'top-right'].includes(position),
        }
      )}>
      {Boolean(video?.desktop) ? (
        <video
          controls={false}
          title='Featured Video'
          height={isMobView ? 'auto' : '100%'}
          width={isMobView ? '100%' : 'auto'}
          autoPlay
          playsInline
          loop
          muted
          className={cx(
            'tw-absolute tw-inset-0 tw-z-0 tw-h-full tw-w-full tw-object-cover tw-object-center',
            {
              'tw-rounded-3xl': isEnhancedLifestyleHeader,
            }
          )}
          poster={(Boolean(image.mobile) && isMobView ? image.mobile.url : image.desktop.url) ?? null}>
          <source
            src={Boolean(video.mobile) && isMobView ? video.mobile.url : video.desktop.url}
            type='video/mp4'
          />
        </video>
      ) : (
        <img
          className={cx(
            'tw-absolute tw-inset-0 tw-z-0 tw-h-full tw-w-full tw-object-cover tw-object-center',
            {
              'tw-rounded-3xl': isEnhancedLifestyleHeader,
            }
          )}
          src={isMobView ? image.mobile.url : image.desktop.url}
        />
      )}

      <span
        className={cx('tw-absolute tw-inset-0 tw-z-10 tw-bg-gradient-to-b tw-from-[#00000076]', {
          'tw-rounded-3xl': isEnhancedLifestyleHeader,
        })}></span>

      <div
        className={cx('tw-relative tw-z-20 tw-flex tw-items-center tw-text-center', {
          'tw-mx-auto tw-inline-flex tw-w-full tw-max-w-[1440px] tw-pb-3 tw-pl-5': [
            'bottom-left',
            'left',
            'top-left',
          ].includes(position),
          'tw-items-end': ['bottom-left', 'bottom-right'].includes(position),
          'tw-items-start': ['top-left', 'top-right'].includes(position),
        })}>
        <h1
          className={cx(
            'tw-font-screamer tw-text-[64px] tw-font-bold tw-uppercase tw-leading-[64px] tw-text-white tw-drop-shadow-md lg:tw-text-[128px] lg:tw-leading-[128px]',
            {
              'tw-text-left': ['bottom-left', 'left', 'top-left'].includes(position),
              'tw-text-right': ['bottom-right', 'right', 'top-right'].includes(position),
            }
          )}>
          <Markdown source={title} className='' unwrapParagraphs />
        </h1>
      </div>
    </div>
  );
};

export default memo(LifestyleHeader);
